import {
  Eye, GraduationCap, Wine, PawPrint, TreePine,
  Moon, ShoppingBasket, Users, Map, Sparkles,
  type LucideIcon,
} from 'lucide-react';
import type { EventFormData } from '@/components/events/eventFormSchema';

/**
 * Activity Template Library v1
 *
 * Templates are starting points, not locked patterns.
 * Every field remains fully editable after selection.
 *
 * Sort order can be influenced by producer_offerings and place_core.intents
 * via `relevantOfferings` — matching templates float to the top.
 */

export interface ActivityTemplate {
  id: string;
  label: string;
  description: string;
  icon: LucideIcon;
  /** Offering keys that make this template more relevant */
  relevantOfferings: string[];
  /** Partial form values to prefill */
  prefill: Partial<EventFormData>;
}

export const ACTIVITY_TEMPLATES: ActivityTemplate[] = [
  // ── Top 5 (always visible) ──
  {
    id: 'farm-tour',
    label: 'Gårdsvisning',
    description: 'Visa upp gården och berätta er historia för besökare.',
    icon: Eye,
    relevantOfferings: ['farm_visit', 'guided_tour', 'participate_open_farm'],
    prefill: {
      title: 'Gårdsvisning',
      description: '<p>Välkommen till vår gård! Under besöket får ni se hur vi jobbar, träffa våra djur och lära er mer om livet på landet.</p>',
      event_type: 'event',
      is_free: true,
      max_spots: 15,
      booking_mode: 'optional',
    },
  },
  {
    id: 'course-workshop',
    label: 'Kurs / workshop',
    description: 'Lär ut ett hantverk eller en färdighet i en praktisk miljö.',
    icon: GraduationCap,
    relevantOfferings: ['learn_course', 'learn_workshop', 'learn_tasting'],
    prefill: {
      title: 'Kurs på gården',
      description: '<p>En praktisk kurs där du får lära dig av oss. Vi går igenom grunderna, och du får prova själv i lugn och ro.</p>',
      event_type: 'course',
      is_free: false,
      price_sek: 500,
      max_spots: 10,
      booking_mode: 'required',
    },
  },
  {
    id: 'tasting',
    label: 'Smakprovning',
    description: 'Bjud in till provning av era produkter – ost, vin, öl eller annat.',
    icon: Wine,
    relevantOfferings: ['eat_drink_tasting', 'eat_drink_wine', 'eat_drink_beer', 'learn_tasting'],
    prefill: {
      title: 'Smakprovning',
      description: '<p>Prova våra produkter och lär er mer om smak, råvaror och hantverk. Vi berättar historien bakom varje smak.</p>',
      event_type: 'event',
      is_free: false,
      price_sek: 350,
      max_spots: 12,
      booking_mode: 'required',
    },
  },
  {
    id: 'meet-animals',
    label: 'Träffa djuren',
    description: 'Låt besökare komma nära djuren på gården.',
    icon: PawPrint,
    relevantOfferings: ['nature_animals_meet', 'nature_animals_farm', 'participate_open_farm'],
    prefill: {
      title: 'Träffa djuren',
      description: '<p>Kom och hälsa på våra djur! Perfekt för familjer och alla som vill uppleva gårdslivet på nära håll.</p>',
      event_type: 'event',
      is_free: true,
      max_spots: 20,
      booking_mode: 'optional',
    },
  },
  {
    id: 'nature-experience',
    label: 'Naturupplevelse',
    description: 'Erbjud en upplevelse i naturen – vandring, skog eller trädgård.',
    icon: TreePine,
    relevantOfferings: ['nature_animals_nature', 'nature_animals_garden', 'nature_animals_hiking'],
    prefill: {
      title: 'Naturupplevelse',
      description: '<p>En stund i naturen med oss som guider. Vi delar med oss av det bästa med vår omgivning.</p>',
      event_type: 'event',
      is_free: true,
      max_spots: 15,
      booking_mode: 'optional',
    },
  },

  // ── Extended 5 ("Visa fler idéer") ──
  {
    id: 'evening',
    label: 'Kväll på gården',
    description: 'En atmosfärisk upplevelse med middag, musik eller berättande.',
    icon: Moon,
    relevantOfferings: ['eat_drink_dinner', 'eat_drink_brunch', 'participate_event'],
    prefill: {
      title: 'Kväll på gården',
      description: '<p>En speciell kväll på gården med god mat, dryck och en avkopplande atmosfär under bar himmel.</p>',
      event_type: 'event',
      is_free: false,
      price_sek: 600,
      max_spots: 30,
      booking_mode: 'required',
    },
  },
  {
    id: 'farm-visit-shop',
    label: 'Gårdsbesök + butik',
    description: 'Kombinera ett besök med möjlighet att handla direkt från gården.',
    icon: ShoppingBasket,
    relevantOfferings: ['shop_farm_shop', 'shop_farm_sale', 'farm_visit'],
    prefill: {
      title: 'Gårdsbesök med gårdsbutik',
      description: '<p>Besök gården, se hur vi jobbar och passa på att handla med er hem av våra produkter direkt från gårdsbutiken.</p>',
      event_type: 'event',
      is_free: true,
      max_spots: 20,
      booking_mode: 'none',
      includes_farm_sale: true,
    },
  },
  {
    id: 'family',
    label: 'Familjeaktivitet',
    description: 'En upplevelse som passar hela familjen, inklusive barn.',
    icon: Users,
    relevantOfferings: ['participate_family', 'nature_animals_meet', 'participate_open_farm'],
    prefill: {
      title: 'Familjedag på gården',
      description: '<p>En härlig dag för hela familjen. Barnen kan klappa djur, plocka bär eller prova på hantverk – medan de vuxna njuter av gårdens lugn.</p>',
      event_type: 'event',
      is_free: true,
      max_spots: 25,
      booking_mode: 'optional',
    },
  },
  {
    id: 'guided-walk',
    label: 'Guidning / vandring',
    description: 'En guidad tur i naturen eller genom gårdens historia.',
    icon: Map,
    relevantOfferings: ['nature_animals_hiking', 'guided_tour', 'nature_animals_nature'],
    prefill: {
      title: 'Guidad vandring',
      description: '<p>Följ med på en guidad tur i våra omgivningar. Vi berättar om naturen, historien och det som gör den här platsen speciell.</p>',
      event_type: 'event',
      is_free: false,
      price_sek: 200,
      max_spots: 12,
      booking_mode: 'required',
    },
  },
  {
    id: 'seasonal-event',
    label: 'Säsong / specialevent',
    description: 'Kosläpp, julmarknad, skördefest eller annat säsongsbundet.',
    icon: Sparkles,
    relevantOfferings: ['participate_event', 'participate_seasonal', 'participate_market'],
    prefill: {
      title: 'Säsongsevent',
      description: '<p>Fira säsongen med oss! Ett särskilt tillfälle att uppleva gården i sitt bästa skick.</p>',
      event_type: 'event',
      is_free: true,
      max_spots: 50,
      booking_mode: 'optional',
    },
  },
];

/** First 5 are "always visible", the rest are "show more" */
export const INITIAL_TEMPLATE_COUNT = 5;

/**
 * Sort templates so that those matching the producer's offerings float to top.
 * Does NOT remove any templates — only reorders.
 */
export function sortTemplatesByRelevance(
  templates: ActivityTemplate[],
  offeringKeys: string[],
): ActivityTemplate[] {
  if (!offeringKeys.length) return templates;
  const keySet = new Set(offeringKeys);

  return [...templates].sort((a, b) => {
    const aScore = a.relevantOfferings.filter(k => keySet.has(k)).length;
    const bScore = b.relevantOfferings.filter(k => keySet.has(k)).length;
    return bScore - aScore; // higher score first
  });
}
